import { useState, useMemo, useCallback, memo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebounce } from '../hooks/useDebounce';
import TypeBadge from '../components/TypeBadge';
import SearchQuery from '../components/SearchQuery';
import { parseQuery } from '../utils/queryParser';
import { filterPokemon, getAllAbilities } from '../utils/pokemonFilter';
import './PokemonDatabase.css';

// ── Constants ────────────────────────────────────────────────────────────────

const STAT_ORDER  = ['hp', 'atk', 'def', 'spa', 'spd', 'spe'];
const STAT_LABELS = { hp: 'HP', atk: 'Atk', def: 'Def', spa: 'SpA', spd: 'SpD', spe: 'Spe' };
const MAX_STATS   = { hp: 255, atk: 190, def: 230, spa: 194, spd: 230, spe: 180 };
const PAGE_SIZE   = 48; // more cards per page since no API round-trip

// Build ability list once at module level
const ALL_ABILITIES = getAllAbilities();

// ── PokemonCard ──────────────────────────────────────────────────────────────

const PokemonCard = memo(function PokemonCard({ pokemon, onNavigate }) {
  const genLabel = pokemon.gen ? `Gen ${pokemon.gen}` : null;

  return (
    <article
      className="pokedex__card"
      onClick={() => onNavigate(pokemon.key)}
      style={{ cursor: 'pointer', borderTop: `3px solid ${typeColor(pokemon.types[0])}` }}
    >
      <div className="pokedex__card-image">
        <img
          src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${pokemon.spriteId}.png`}
          alt={pokemon.name}
          loading="lazy"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${pokemon.spriteId}.png`;
          }}
        />
      </div>

      <div className="pokedex__card-info">
        {/* Name + badges row */}
        <div className="pokedex__name-row">
          <span className="pokedex__id">#{String(pokemon.id).padStart(4, '0')}</span>
          <h2>{pokemon.name}</h2>
          <div className="pokedex__badges">
            {genLabel && <span className="pokedex__gen-badge">{genLabel}</span>}
            {pokemon.isLegendary && <span className="pokedex__meta-badge pokedex__meta-badge--legendary">★</span>}
            {pokemon.isMythical  && <span className="pokedex__meta-badge pokedex__meta-badge--mythical">✦</span>}
          </div>
        </div>

        {/* Types */}
        <div className="pokedex__types">
          {pokemon.types.map(type => (
            <TypeBadge key={type} type={type} />
          ))}
        </div>

        {/* Stat bars */}
        <div className="pokedex__stats">
          {STAT_ORDER.map(stat => {
            const val = pokemon.baseStats[stat];
            const pct = Math.min((val / MAX_STATS[stat]) * 100, 100);
            const color = pct > 66 ? '#2ecc71' : pct > 33 ? '#f1c40f' : '#e74c3c';
            return (
              <div key={stat} className="pokedex__stat">
                <label className="pokedex__stat-label">{STAT_LABELS[stat]}</label>
                <div className="pokedex__stat-bar">
                  <div
                    className="pokedex__stat-bar-fill"
                    style={{ width: `${pct}%`, '--stat-color': color }}
                  />
                </div>
                <div className="pokedex__stat-value">{val}</div>
              </div>
            );
          })}
        </div>

        {/* BST */}
        <div className="pokedex__bst">
          BST <span className="pokedex__bst-value">{pokemon.bst}</span>
        </div>
      </div>
    </article>
  );
});

// Minimal type→color mapping (matches TypeBadge colours)
const TYPE_COLORS = {
  Normal: '#A8A878', Fire: '#F08030', Water: '#6890F0', Electric: '#F8D030',
  Grass: '#78C850', Ice: '#98D8D8', Fighting: '#C03028', Poison: '#A040A0',
  Ground: '#E0C068', Flying: '#A890F0', Psychic: '#F85888', Bug: '#A8B820',
  Rock: '#B8A038', Ghost: '#705898', Dragon: '#7038F8', Dark: '#705848',
  Steel: '#B8B8D0', Fairy: '#EE99AC',
};
function typeColor(type) { return TYPE_COLORS[type] ?? '#999'; }

// ── PokemonDatabase page ─────────────────────────────────────────────────────

function PokemonDatabase() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Query string synced to URL ?q=
  const rawQuery = searchParams.get('q') ?? '';
  const [localQuery, setLocalQuery] = useState(rawQuery);
  const debouncedQuery = useDebounce(localQuery, 180);

  const [page, setPage] = useState(1);

  // Update URL when debounced query changes
  useMemo(() => {
    const current = searchParams.get('q') ?? '';
    if (debouncedQuery !== current) {
      setSearchParams(debouncedQuery ? { q: debouncedQuery } : {}, { replace: true });
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  // Filter + sort (memoised — only re-runs when query changes)
  const allResults = useMemo(
    () => filterPokemon(parseQuery(debouncedQuery)),
    [debouncedQuery]
  );

  // Pagination
  const totalPages  = Math.max(1, Math.ceil(allResults.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageResults = allResults.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleQueryChange = useCallback((q) => {
    setLocalQuery(q);
    setPage(1);
  }, []);

  const handleNavigate = useCallback((key) => navigate(`/pokemon/${key}`), [navigate]);

  const handlePageChange = useCallback((p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="pokedex">
      <header className="pokedex__header">
        <h1>Pokédex</h1>
        <SearchQuery
          value={localQuery}
          onChange={handleQueryChange}
          resultCount={allResults.length}
          allAbilities={ALL_ABILITIES}
        />

        {/* Top pagination */}
        <Pagination page={currentPage} totalPages={totalPages} onChange={handlePageChange} />
      </header>

      {/* Results grid */}
      {allResults.length === 0 ? (
        <div className="pokedex__no-results">
          <p>No Pokémon match your search.</p>
          <button onClick={() => handleQueryChange('')}>Clear search</button>
        </div>
      ) : (
        <div className="pokedex__grid">
          {pageResults.map(pokemon => (
            <PokemonCard key={pokemon.id} pokemon={pokemon} onNavigate={handleNavigate} />
          ))}
        </div>
      )}

      {/* Bottom pagination */}
      <Pagination page={currentPage} totalPages={totalPages} onChange={handlePageChange} />
    </div>
  );
}

// ── Pagination ───────────────────────────────────────────────────────────────

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="pokedex__pagination">
      <button onClick={() => onChange(Math.max(1, page - 1))} disabled={page === 1}>
        ← Prev
      </button>
      <span>Page {page} of {totalPages}</span>
      <button onClick={() => onChange(Math.min(totalPages, page + 1))} disabled={page === totalPages}>
        Next →
      </button>
    </div>
  );
}

export default PokemonDatabase;
